import DonationCard from '@src/components/Organism/Card/DonationCard';
import DonationEmpty from '@src/components/Organism/Card/DonationEmpty';
import DonationListHeader from '@src/components/Organism/Card/DonationListHeader';
import Pagination from '@src/components/Molecules/Pagination';
import { useOpenDonationChat } from '@src/hooks/useChat';
import { usePagedDonations } from '@src/hooks/useCommunitys';
import { IContent } from '@src/interfaces/community';
import PageTemplate from '@src/components/Template/Page';
import Skeleton from '@src/components/LoadingSkeleton';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import keywordAtom from '@src/recoils/atom/keyword';
import { aboSort, bloodProductSort, region, sort } from '@src/utils/community';
import filterAtom from '@src/recoils/atom/filter';
import ChatStartModal from '@src/components/Organism/Modal/ChatStartModal';
import Meta from '@src/components/Meta';
import { metaInfo } from '@src/data/meta';
import { communityKeys } from '@src/keys/communityKey';
import { community as communityApi } from '@src/services/api';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';

/**
 * 첫 화면(필터 미적용·1페이지)만 서버에서 미리 채운다.
 * 클라이언트의 usePagedDonations 기본 키와 동일해야 하이드레이션에서 재요청이 없다.
 */
export const getServerSideProps: GetServerSideProps = async () => {
  const queryClient = new QueryClient();
  const key = [
    ...communityKeys.donationPage('', '', '', 'LAST_RENEWED_AT'),
    '',
    '',
    0,
  ];
  await queryClient.prefetchQuery(key, () =>
    communityApi.getDonations(0, '', '', '', 'LAST_RENEWED_AT', '', false)
  );
  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};

/**
 * 헌혈요청 목록 (Pple Blood Request List 디자인).
 * 무한스크롤 → 디자인의 페이지네이션으로 교체했다.
 * (기존 useDonations는 queryFn이 pageParam을 무시해 0페이지만 반복 로드하던 상태였다.)
 */
const CommunityPage = () => {
  const { title, description, ogImage } = metaInfo.donation;
  // 카드의 '대화하기' — 상세로 들어가지 않고 목록에서 바로 쪽지방을 연다(디자인).
  const { openChat, isOpening } = useOpenDonationChat();
  const [keyword, setKeyword] = useRecoilState(keywordAtom);
  const [filterList, setFilterList] = useRecoilState(filterAtom);
  const [page, setPage] = useState(0);
  const [urgentOnly, setUrgentOnly] = useState(false);
  // 디자인: 카드의 '대화하기'는 확인 모달을 거친다.
  const [chatTarget, setChatTarget] = useState<any>(null);

  // region처럼 초기 atom에 없는 필터도 있으므로 옵셔널로 읽는다(없으면 빈 문자열).
  const filterLabel = (type: string) =>
    filterList.find((e) => e.type === type)?.label ?? '';

  const aboParam = aboSort(filterLabel('abo'));
  const productParam = bloodProductSort(filterLabel('bloodProduct'));
  const sortParam = sort(filterLabel('sortBy'));
  const regionParam = region(filterLabel('region'));

  const { page: result, isLoading } = usePagedDonations(
    page,
    keyword,
    aboParam,
    productParam,
    sortParam,
    regionParam,
    urgentOnly
  );

  // 필터가 바뀌면 1페이지로 되돌린다.
  useEffect(() => {
    setPage(0);
  }, [keyword, aboParam, productParam, sortParam, regionParam, urgentOnly]);

  const resetFilters = () => {
    setFilterList([]);
    setKeyword('');
    setUrgentOnly(false);
    setPage(0);
  };

  const total = result?.totalElements;
  const isEmpty = !isLoading && total === 0;

  return (
    <>
      <Meta title={title} description={description} ogImage={ogImage} />
      <PageTemplate
        // 기본 셀렉터 필터와 우측 Aside(검색·요청글쓰기·후원 배너)는
        // 새 헤더(DonationListHeader)와 중복이고 디자인엔 사이드바가 없다.
        isSide={false}
        hideAside
        maxWidth={1120}
        href={'/community/donation'}
        mainText={'헌혈요청'}
        isLoading={isLoading}
      >
        <DonationListHeader
          totalCount={total}
          urgentOnly={urgentOnly}
          onToggleUrgent={() => setUrgentOnly((v) => !v)}
        />
        {isLoading &&
          [0, 1, 2, 3, 4].map((e) => <Skeleton.LoadingSkeletonCard key={e} />)}
        {!isLoading &&
          result?.content?.map((community: IContent) => (
            <DonationCard
              key={community.uuid}
              community={community}
              onChat={setChatTarget}
              chatDisabled={isOpening}
            />
          ))}
        {isEmpty && <DonationEmpty onReset={resetFilters} />}
        {chatTarget && (
          <ChatStartModal
            isLoading={isOpening}
            onClose={() => setChatTarget(null)}
            stopPropagation={(e) => e.stopPropagation()}
            onConfirm={() => {
              const target = chatTarget;
              setChatTarget(null);
              openChat(target);
            }}
          />
        )}
        {!isLoading && !isEmpty && (
          <Pagination
            page={page}
            totalPages={result?.totalPages ?? 0}
            onChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        )}
      </PageTemplate>
    </>
  );
};

export default CommunityPage;
